import { DataRefreshEvent } from "@fp/admin-api-client";
import {
  closeOpportunity,
  createOpportunityTemplate,
  deleteOpportunity,
  getGetOpportunityByIdQueryKey,
  getGetOpportunityByIdQueryOptions,
  getGetOpportunityClaimsQueryKey,
  getGetOpportunityClaimsQueryOptions,
  getGetOpportunitySharesQueryKey,
  getGetOpportunitySharesQueryOptions,
  shareOpportunity,
} from "@fp/admin-api-client/client";
import { ConfirmationModal } from "@fp/ui/components/application/modals/confirmation-modal";
import { DestructiveConfirmationModal } from "@fp/ui/components/application/modals/destructive-confirmation-modal";
import { TextInputModal } from "@fp/ui/components/application/modals/text-input-modal";
import { Button } from "@fp/ui/components/base/buttons/button";
import {
  PageContentContainer,
  PageLoading,
} from "@fp/ui/components/base/page-layout/page-layout";
import { OpportunityDetail } from "@fp/ui/components/opportunities/opportunity-detail";
import { useQueryClient } from "@tanstack/react-query";

import { createFileRoute, useRouter } from "@tanstack/react-router";
import { Edit05, File06, Trash01, UserPlus01, XCircle } from "@untitledui/icons";
import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";
import { AdminLayout } from "@/components/admin-layout";
import { AdminPageHeader } from "@/components/admin-page-header";
import {
  type PageAction,
  useSetPageActions,
} from "@/components/page-actions-context";
import { PageActionsMenu } from "@/components/page-actions-menu";
import { useDataRefresh } from "@/hooks/useSignalR";

export const Route = createFileRoute("/opps/$oppId/")({
  component: RouteComponent,
  loader: async ({ context: { queryClient }, params }) => {
    const [opportunity, shares, claims] = await Promise.all([
      queryClient.fetchQuery(getGetOpportunityByIdQueryOptions(params.oppId)),
      queryClient.fetchQuery(getGetOpportunitySharesQueryOptions(params.oppId)),
      queryClient.fetchQuery(getGetOpportunityClaimsQueryOptions(params.oppId)),
    ]);

    return {
      opportunity: opportunity.data,
      shares: shares.data.items || [],
      claims: claims.data.items || [],
    };
  },
  pendingComponent: () => <PageLoading />,
});

function RouteComponent() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { oppId } = Route.useParams();
  const { opportunity, shares, claims } = Route.useLoaderData();
  const invalidateOpportunity = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: getGetOpportunityByIdQueryKey(oppId),
    });
    queryClient.invalidateQueries({
      queryKey: getGetOpportunityClaimsQueryKey(oppId),
    });
    queryClient.invalidateQueries({
      queryKey: getGetOpportunitySharesQueryKey(oppId),
    });
    router.invalidate();
  }, [queryClient, oppId, router]);
  useDataRefresh(DataRefreshEvent.OpportunityClaimed, invalidateOpportunity);
  useDataRefresh(DataRefreshEvent.OpportunityShared, invalidateOpportunity);
  useDataRefresh(DataRefreshEvent.AddressGeocodeCompleted, invalidateOpportunity);
  useDataRefresh(DataRefreshEvent.PayoutRequestSubmitted, invalidateOpportunity);
  useDataRefresh(DataRefreshEvent.PayoutRequestReturned, invalidateOpportunity);
  useDataRefresh(DataRefreshEvent.PayoutRequestApproved, invalidateOpportunity);
  useDataRefresh(DataRefreshEvent.PayoutRequestDenied, invalidateOpportunity);
  useDataRefresh(DataRefreshEvent.PayoutRequestPaid, invalidateOpportunity);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const canEdit = claims.length === 0 || opportunity.status === "Draft";
  const isDraft = opportunity.status === "Draft";
  const canDelete = isDraft;
  const canClose = opportunity.status !== "Closed";

  useSetPageActions(
    "Opp Actions",
    useMemo(() => {
      const actions: PageAction[] = [
        {
          id: "save-as-template",
          icon: File06,
          label: "Save as Template",
          onAction: () => setShowSaveTemplateModal(true),
        },
      ];
      if (canEdit) {
        actions.push({
          id: "edit",
          icon: Edit05,
          label: "Edit",
          onAction: () =>
            router.navigate({
              to: "/opps/$oppId/edit",
              params: { oppId: opportunity.id },
            }),
        });
      }
      actions.push({
        id: "invite-from-roster",
        icon: UserPlus01,
        label: "Invite from Roster",
        onAction: () =>
          router.navigate({
            to: "/opps/$oppId/edit/invite",
            params: { oppId },
            state: { returnTo: `/opps/${oppId}` },
          }),
      });
      if (canClose) {
        actions.push({
          id: "close",
          icon: XCircle,
          label: "Close",
          className: "[&_span]:text-error-primary [&_svg]:text-error-primary",
          separatorBefore: true,
          onAction: () => setShowCloseModal(true),
        });
      }
      if (canDelete) {
        actions.push({
          id: "delete",
          icon: Trash01,
          label: "Delete",
          className: "[&_span]:text-error-primary [&_svg]:text-error-primary",
          onAction: () => setShowDeleteModal(true),
        });
      }
      return actions;
    }, [canEdit, canClose, canDelete, oppId, opportunity.id, router]),
  );

  const handleShare = async () => {
    setIsSharing(true);
    try {
      await shareOpportunity(oppId);
      toast.success("Opp shared successfully");
      queryClient.invalidateQueries({
        queryKey: getGetOpportunityByIdQueryOptions(oppId).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: ["opportunities"],
      });
      router.invalidate();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { title?: string } } };
      toast.error(err.response?.data?.title || "Failed to share opp");
    } finally {
      setIsSharing(false);
    }
  };

  const handleClose = async () => {
    setIsClosing(true);
    try {
      await closeOpportunity(oppId);
      setShowCloseModal(false);
      toast.success("Opp closed successfully");
      queryClient.invalidateQueries({
        queryKey: getGetOpportunityByIdQueryOptions(oppId).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: ["opportunities"],
      });
      router.invalidate();
    } catch (error: unknown) {
      const err = error as { response?: { data?: { title?: string } } };
      toast.error(err.response?.data?.title || "Failed to close opp");
    } finally {
      setIsClosing(false);
    }
  };

  const handleSaveAsTemplate = async (templateName: string) => {
    setIsSavingTemplate(true);
    try {
      await createOpportunityTemplate({
        templateName,
        title: opportunity.title,
        description: opportunity.description,
        type: opportunity.type,
        requireGpsCheckIn: opportunity.requireGpsCheckIn,
        enableGpsCheckIn: opportunity.enableGpsCheckIn,
        referenceRequirements: opportunity.referenceRequirements.map((requirement) => ({ label: requirement.label, masks: requirement.masks, hint: requirement.hint })),
        requireImage: opportunity.requireImage,
        requireDescription: opportunity.requireDescription,
        requireTimeSheet: opportunity.requireTimeSheet,
        payoutInstructions: opportunity.payoutInstructions,
        startDate: opportunity.startDate,
        endDate: opportunity.endDate,
        payRate: opportunity.payRate ?? undefined,
        numberOfUnits: opportunity.numberOfUnits,
        maxUnitsPerParticipant: opportunity.maxUnitsPerParticipant,
        maxClaimsPerRoster: opportunity.maxClaimsPerRoster,
        address: opportunity.address ?? undefined,
        isShareable: opportunity.isShareable,
        url: opportunity.url,
        isActive: true,
      });
      setShowSaveTemplateModal(false);
      toast.success("Template saved");
    } catch {
      toast.error("Failed to save template");
    } finally {
      setIsSavingTemplate(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await deleteOpportunity(oppId);
      setShowDeleteModal(false);
      router.navigate({
        to: "/opps/categories/$category",
        params: { category: "drafts" },
      });
      queryClient.invalidateQueries({
        queryKey: getGetOpportunityByIdQueryOptions(oppId).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: ["opportunities"],
      });
      queryClient.invalidateQueries({
        queryKey: getGetOpportunitySharesQueryKey(oppId),
      });
      router.invalidate();
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AdminLayout>
      <AdminPageHeader
        title="Opp Detail"
        parent={{ url: "/opps", label: "Opps" }}
        titleActions={<PageActionsMenu />}
      />
      <PageContentContainer className="flex flex-col flex-1 space-y-6">
        <OpportunityDetail
          {...opportunity}
          shares={shares}
          claims={claims}
          onClaimPayoutRequestedClick={(claimId) =>
            router.navigate({
              to: "/opportunity-claims/$claimId/timeline",
              params: { claimId },
              state: {
                returnTo: `/opps/${oppId}`,
                returnLabel: "Opp Detail",
              },
            })
          }
          onRemoveTalentMember={async () => {
            await queryClient.invalidateQueries({
              queryKey: getGetOpportunityByIdQueryKey(oppId),
            });
            await queryClient.invalidateQueries({
              queryKey: getGetOpportunitySharesQueryKey(oppId),
            });
            await queryClient.invalidateQueries({
              queryKey: getGetOpportunityClaimsQueryKey(oppId),
            });
            await router.invalidate();
          }}
          onAddTalentMember={() =>
            router.navigate({
              to: "/opps/$oppId/edit/invite",
              params: { oppId },
              state: { returnTo: `/opps/${oppId}` },
            })
          }
        />
        {isDraft && (
          <div className="mt-auto pt-6">
            <Button
              color="primary"
              size="xl"
              className="w-full"
              onClick={handleShare}
              isDisabled={isSharing}
            >
              {isSharing ? "Sharing..." : "Share"}
            </Button>
          </div>
        )}
      </PageContentContainer>
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Draft"
        message="Are you sure you want to delete this draft opportunity?"
        confirmText="Delete"
        cancelText="Cancel"
        isLoading={isDeleting}
      />
      <DestructiveConfirmationModal
        isOpen={showCloseModal}
        onClose={() => setShowCloseModal(false)}
        onConfirm={handleClose}
        title="Close Opp"
        entity={{ label: opportunity.title }}
        warningText="Closing this opp removes it from participants and stops new claims."
        confirmText="Close Opp"
        isLoading={isClosing}
      />
      <TextInputModal
        isOpen={showSaveTemplateModal}
        onClose={() => setShowSaveTemplateModal(false)}
        onConfirm={handleSaveAsTemplate}
        title="Save as Template"
        message="Save this opportunity as a reusable template for your organization."
        label="Template Name"
        placeholder="e.g. Weekend Delivery Driver"
        confirmText="Save Template"
        isLoading={isSavingTemplate}
      />
    </AdminLayout>
  );
}
